import React, { useRef, useState, useEffect } from "react";
import styles from "styles.module.css";
//@ts-ignore
import "./style.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addWeeks } from "date-fns";
import { useTranslation } from "react-i18next";
import { salesStatisticsGridColumnItems } from "./GridColumns";
import { getSalesStatisticsApi, SalesStatisticsFilter } from "api/sales";
import ColumnDropdown from "./utils/dropdown";
import Filter from "./Filter";
import { SalesStatistics } from "interface/sales";
import { useInfoStore } from "store/error";
import { CustomError } from "interface";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { OverLayLoading } from "ui/Loading";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";

import { usePageAccess } from "hooks/usePageAccess";
import { aggregatesFields } from "./utils/data";
import { GroupHeaderCellGrossWeight } from "ui/grid/CustomGrid";
import { Loading } from "ui/Loading";
import SalesStatisticsMobileList from "./SalesStatisticsMobileList";

const pageSize = 60;

const SalesStatisticsPage = () => {
	const { t } = useTranslation();
	const isInitialMobileView =
		typeof window !== "undefined" && window.innerWidth < 768;

	const gridRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [allColumns, setAllColumns] = useState<GridColumnProps[]>([]);
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const [refReady, setRefReady] = useState<boolean>(false);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [isLoading, setLoading] = useState<boolean>(false);
	const headerRef = useRef<HTMLImageElement | null>(null);
	const [data, setData] = useState<SalesStatistics[]>([]);

	const { setError } = useInfoStore();

	const [filterOptions, setFilterOptions] = useState<SalesStatisticsFilter>({
		take: pageSize,
		skip: 0,
		fromDate: isInitialMobileView ? new Date() : addWeeks(new Date(), -7),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const { pageAccess } = usePageAccess();

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (headerRef.current) {
			setRefReady(true);
		}
	}, [headerRef.current]);

	useEffect(() => {
		const updateColumnWidths = () => {
			if (allColumns.length === 0) return;
			const grid = document.querySelector(".k-grid") as HTMLElement;
			const table = document.querySelector(".k-table") as HTMLElement;
			if (!grid || !table) return;

			const tableWidth = table.offsetWidth;
			const gridWidth = grid.offsetWidth;
			if (!gridWidth || !tableWidth) return;

			const addingWidth = gridWidth - tableWidth;
			const productWidthDefault = 200;
			let incrementedWidth = 0;
			const incrementValue = 10;

			if (addingWidth < -10) {
				let updatedNeeded = false;
				const updatedColumns = allColumns.map((column) => {
					if (
						column.field === "productDescription" &&
						parseWidth(column.width) > productWidthDefault
					) {
						updatedNeeded = true;
						const width = parseWidth(column.width) - Math.abs(addingWidth);
						column.width = Math.max(width, productWidthDefault);
					}
					return column;
				});

				if (updatedNeeded) {
					setAllColumns(updatedColumns);
				}
				return;
			}
			if (addingWidth <= 0) return;

			let isProductDescriptionColumnHidden = allColumns.find(
				(col) => col.field === "productDescription",
			)?.hidden;

			const updatedColumns = allColumns.map((column) => {
				let newColumn = { ...column };

				if (isProductDescriptionColumnHidden) {
					// Check if the column is hidden and increment width of other columns
					if (!newColumn.hidden && incrementedWidth < addingWidth) {
						//increment other columns width by 10px all total incrementedWidth  = newWidth
						incrementedWidth += incrementValue; // Increment the width by 10px

						newColumn.width = parseWidth(newColumn.width) + incrementValue; // Update the column width
					}
				} else {
					if (newColumn.field === "productDescription") {
						//add needed width to productDescription column
						let width = parseWidth(newColumn.width);
						if (width > productWidthDefault) {
							width = productWidthDefault;
						}

						newColumn.width = width + addingWidth;
					}
				}
				return newColumn;
			});

			if (updatedColumns.length === 0) {
				console.error("No columns to update widths for.");
				return;
			}
			setAllColumns(updatedColumns);
		};

		updateColumnWidths();

		window.addEventListener("resize", updateColumnWidths);
		return () => window.removeEventListener("resize", updateColumnWidths);
	}, [data, forceUpdate]);

	useEffect(() => {
		setAllColumns(
			salesStatisticsGridColumnItems({
				ref: headerRef,
				data: data,
				pageAccess: pageAccess,
			}),
		);
	}, [pageAccess, refReady, data]);

	useEffect(() => {
		setLoading(true);
		getSalesStatisticsApi(filterOptions)
			.then((data) => {
				setData(data.data);
				setForceUpdate((prev) => !prev);
			})
			.catch((error) => {
				if (error.response?.data) {
					setError(new CustomError("Something went wrong", "error"));
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [filterOptions]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const handleCheckboxChange = (field: string, checked: boolean) => {
		setAllColumns((prevItems) =>
			prevItems.map((item) =>
				item.field === field ? { ...item, hidden: checked } : item,
			),
		);
		setForceUpdate((prev) => !prev);
	};

	const closeColumnDropdown = () => setShowPopup(false);

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div className={styles.PageContainer} style={{}}>
			{!isMobileView && isLoading && <OverLayLoading show={isLoading} />}
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("SalesStatistics")}
				</legend>
			</div>

			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<SalesStatisticsMobileList items={data} loading={isLoading} />
				) : (
					<GridWithGroupPaging
						gridData={data}
						gridColumns={allColumns}
						itemAboveHeight={90}
						dataKey="contractNoteNumber"
						className="sales-statistics-grid"
						exportGridAsExcelRef={excelRef}
						showSelectedFilters={true}
						loading={isLoading}
						sortable={true}
						gridStyle={{ width: "100%" }}
						excelProps={{
							fileName: `sales_statistics_${new Date().toISOString()}.xlsx`,
						}}
						groupable={{ footer: "visible", enabled: true }}
						groupHeaderCell={GroupHeaderCellGrossWeight}
						aggregateFields={aggregatesFields}
						groupLargeColumn={[
							{ field: "grossWeight", width: 270 },
							{
								field: "buyerPrice",
								width: 270,
							},
						]}
					/>
				)}
			</div>

			{!isMobileView && refReady && headerRef.current && (
				<ColumnDropdown
					show={showPopup}
					onClose={closeColumnDropdown}
					items={allColumns}
					onCheckboxChange={handleCheckboxChange}
					anchor={headerRef}
					forceUpdate={forceUpdate}
				/>
			)}
		</div>
	);
};

export default SalesStatisticsPage;

const parseWidth = (width: string | number | undefined): number => {
	if (width === undefined) return 0;

	if (typeof width === "number") {
		return width;
	}

	if (typeof width === "string") {
		// Remove 'px' and convert to number
		return parseInt(width.replace("px", ""), 10) || 0;
	}

	return 0;
};
